import React, { useEffect, useRef, useState } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';

function Consent() {
  const { token, refreshAccessToken, getValidToken } = useToken();
  const [doc, setDoc]                       = useState(null);
  const [checked, setChecked]               = useState(false);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [submitting, setSubmitting]         = useState(false);
  const [alreadyConsented, setAlreadyConsented] = useState(false);
  const [viewerUrl, setViewerUrl]           = useState(null);
  const viewerUrlRef                        = useRef(null);
  const navigate                            = useNavigate();

  useProgressRedirect('CONSENT');

  useEffect(() => {
    (async () => {
      try {
        const [docRes, statusRes] = await Promise.all([
          fetch('/api/consent',        { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/consent/status', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!docRes.ok) throw new Error('Failed to load consent document.');
        setDoc(await docRes.json());
        if (statusRes.ok) {
          const s = await statusRes.json();
          setAlreadyConsented(Boolean(s?.consented));
        }

        // Auto-load PDF blob for the embedded viewer
        const pdfRes = await fetch('/api/consent/download', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (pdfRes.ok) {
          const url = window.URL.createObjectURL(await pdfRes.blob());
          viewerUrlRef.current = url;
          setViewerUrl(url);
        }
      } catch (err) {
        setError(err.message || 'Failed to load consent info.');
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (viewerUrlRef.current) window.URL.revokeObjectURL(viewerUrlRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDownload = async (e) => {
    e?.preventDefault();
    setError('');
    try {
      const authToken = await getValidToken();
      const res = await fetch('/api/consent/download', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to download consent PDF.');
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const cd   = res.headers.get('content-disposition') || '';
      const m    = /filename="?([^";]+)"?/.exec(cd);
      a.href     = url;
      a.download = m?.[1] ?? 'consent.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download consent.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!alreadyConsented && !checked) {
      setError('Please confirm that you have read and agree to participate.');
      return;
    }
    setSubmitting(true);
    try {
      const authToken = await getValidToken();
      const agreeRes  = await fetch('/api/consent/agree', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body:    JSON.stringify({ version: doc?.version }),
      });
      if (!agreeRes.ok) {
        const err = await agreeRes.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to record consent.');
      }
      const progressRes = await fetch('/api/player/progress', {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body:    JSON.stringify({ progress_step: 'PRE_EXPERIMENT' }),
      });
      if (progressRes.ok) {
        navigate('/pre-experiment', { replace: true });
        return;
      }
      const err = await progressRes.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update progress.');
    } catch (err) {
      setError(err.message || 'Unexpected error submitting consent.');
    }
    setSubmitting(false);
  };

  return (
    <Container className="py-4">
      <ProgressTracker currentStep="consent" />
      {(loading || submitting) && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (
        <Card className="shadow-lg border-0">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="mb-0">📄 Informed Consent</h2>
              <span className="text-muted small">Version: <strong>{doc?.version || 'N/A'}</strong></span>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              {/* Always-visible PDF viewer */}
              <div className="mb-0" style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
                {viewerUrl ? (
                  <iframe
                    title="Consent Document"
                    src={viewerUrl}
                    style={{ width: '100%', height: 380, display: 'block', border: 'none' }}
                  />
                ) : (
                  <div className="text-center py-5 text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading document…
                  </div>
                )}
              </div>

              {/* Checkbox attached directly below the viewer */}
              <div style={{ border: '1px solid #dee2e6', borderTop: 'none', borderRadius: '0 0 4px 4px', padding: '12px 14px', background: '#f8f9fa' }}>
                <Form.Check
                  id="consent-check"
                  type="checkbox"
                  checked={alreadyConsented || checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  label="I have read and understood the information in this document and I have received an answer to all my questions regarding this research. I give my consent to participate."
                  disabled={alreadyConsented}
                />
                {alreadyConsented && (
                  <Alert variant="info" className="mt-2 mb-0 py-2">
                    Consent already recorded. Click Continue to proceed.
                  </Alert>
                )}
              </div>

              {/* Action row */}
              <div className="d-flex gap-2 mt-3">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting || (!alreadyConsented && !checked)}
                >
                  {submitting
                    ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</>
                    : 'Start'}
                </Button>
                <Button variant="outline-secondary" onClick={handleDownload}>
                  Download PDF
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default Consent;
